import React, { useEffect, useMemo, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Slide from "@mui/material/Slide";
import { Box, Button, IconButton, Typography } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { useProducts, useRenewSubscription } from "../../reactQuery/subscription";
import "./subscriptionPopup.scss";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="down" ref={ref} {...props} />;
});

const PLAN_FEATURES = [
  "Kanban boards and task tracking",
  "Custom workflows",
  "Time tracking and worklogs",
  "Reporting and dashboards",
  "Client & contractor access",
];

const FREE_PLAN = {
  id: "free",
  isFree: true,
  name: "FREE",
  price: "0",
  period: "forever",
  description: "Free forever for teams with up to 10 users.",
  features: ["Up to 10 users per company plan", ...PLAN_FEATURES],
};

const PLAN_DETAILS_BY_INTERVAL = {
  month: {
    name: "MONTHLY FLAT FEE",
    period: "/month",
    description: "Per company.",
  },
  year: {
    name: "YEARLY FLAT FEE",
    period: "/year",
    description: "Per company, billed yearly.",
  },
};

const PAID_PLAN_FEATURES = [
  "Up to 200 users per company plan (add another flat-fee block to scale further)",
  ...PLAN_FEATURES,
];

const getProductsPayload = (productsData) => {
  return (
    productsData?.data?.data?.responseData ||
    productsData?.data?.responseData ||
    productsData?.responseData ||
    {}
  );
};

const getProductsFromResponse = (productsData) => {
  const payload = getProductsPayload(productsData);
  return Array.isArray(payload) ? payload : payload.responseData || [];
};

const getCurrentPlanIdFromResponse = (productsData) => {
  const payload = getProductsPayload(productsData);
  return payload.current_plan_id || null;
};

const getPriceUnitAmount = (price) => {
  if (typeof price.unit_amount === "number") {
    return price.unit_amount;
  }

  if (price.unit_amount_decimal) {
    return Number(price.unit_amount_decimal);
  }

  return null;
};

const formatPrice = (unitAmount) => {
  if (typeof unitAmount !== "number" || Number.isNaN(unitAmount)) {
    return "";
  }

  return (unitAmount / 100).toFixed(2);
};

const getRecurringPrices = (productsData) => {
  const products = getProductsFromResponse(productsData);

  return products
    .flatMap((product) => product.prices || [])
    .filter((price) => price.type === "recurring" && price.recurring?.interval);
};

const buildPaidPlans = (prices) =>
  prices.reduce((plans, price) => {
    const interval = price.recurring?.interval;
    const planDetails = PLAN_DETAILS_BY_INTERVAL[interval];
    const unitAmount = getPriceUnitAmount(price);

    if (!planDetails || plans.some((plan) => plan.recurringInterval === interval)) {
      return plans;
    }

    return [
      ...plans,
      {
        id: price.id,
        recurringInterval: interval,
        name: planDetails.name,
        price: formatPrice(unitAmount),
        period: planDetails.period,
        description: planDetails.description,
        features: PAID_PLAN_FEATURES,
        stripePriceId: price.id,
      },
    ];
  }, [])
    .sort(
      (firstPlan, secondPlan) =>
        Object.keys(PLAN_DETAILS_BY_INTERVAL).indexOf(firstPlan.recurringInterval) -
        Object.keys(PLAN_DETAILS_BY_INTERVAL).indexOf(secondPlan.recurringInterval)
    );

const getSavingsLabel = (monthlyPlan, yearlyPlan) => {
  if (!monthlyPlan || !yearlyPlan) return null;

  const monthlyPrice = Number(monthlyPlan.price);
  const yearlyPrice = Number(yearlyPlan.price);

  if (
    Number.isNaN(monthlyPrice) ||
    Number.isNaN(yearlyPrice) ||
    monthlyPrice <= 0 ||
    yearlyPrice <= 0
  ) {
    return null;
  }

  const savings = monthlyPrice * 12 - yearlyPrice;
  return savings > 0 ? `Save $${savings.toFixed(2)}` : null;
};

const buildPlans = (prices, currentPlanId) => {
  const paidPlans = buildPaidPlans(prices);
  const monthlyPlan = paidPlans.find((plan) => plan.recurringInterval === "month");
  const yearlyPlan = paidPlans.find((plan) => plan.recurringInterval === "year");
  const savings = getSavingsLabel(monthlyPlan, yearlyPlan);

  return [
    {
      ...FREE_PLAN,
      isCurrent: !currentPlanId,
    },
    ...paidPlans.map((plan) => ({
      ...plan,
      savings: plan.recurringInterval === "year" ? savings : undefined,
      isCurrent: plan.stripePriceId === currentPlanId,
    })),
  ];
};

const SubscriptionExpiredPopup = ({
  open,
  onClose,
  onSelectPlan,
  disableClose = false,
}) => {
  const [renewingPlanId, setRenewingPlanId] = useState(null);
  const { data: productsData } = useProducts();
  const { mutate: renewSubscription, isPending: isRenewing } = useRenewSubscription();

  const currentPlanId = useMemo(
    () => getCurrentPlanIdFromResponse(productsData),
    [productsData]
  );

  const plans = useMemo(() => {
    const prices = getRecurringPrices(productsData);
    return buildPlans(prices, currentPlanId);
  }, [currentPlanId, productsData]);

  useEffect(() => {
    if (!open) {
      setRenewingPlanId(null);
    }
  }, [open]);

  const handleUpgrade = (plan) => {
    if (plan.isCurrent || isRenewing) return;

    const priceId = plan.stripePriceId;
    if (!priceId) return;

    setRenewingPlanId(plan.id);
    const redirectUrl =
      window.location.origin + window.location.pathname + window.location.search;

    renewSubscription(
      {
        price_id: priceId,
        redirect_url: redirectUrl,
      },
      {
        onSuccess: (response) => {
          const checkoutUrl =
            response?.data?.checkout_url ||
            response?.data?.data?.checkout_url ||
            response?.checkout_url;

          if (checkoutUrl) {
            window.location.href = checkoutUrl;
            return;
          }

          setRenewingPlanId(null);

          if (onSelectPlan && typeof onSelectPlan === "function") {
            onSelectPlan(plan);
          }
        },
        onError: () => {
          setRenewingPlanId(null);

          if (onSelectPlan && typeof onSelectPlan === "function") {
            onSelectPlan(plan);
          }
        },
      }
    );
  };

  const handleClose = () => {
    if (onClose && typeof onClose === "function") {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      keepMounted
      onClose={disableClose ? undefined : handleClose}
      maxWidth={false}
      fullWidth
      disableEscapeKeyDown={disableClose}
      className="subscription-expired-dialog"
    >
      {onClose && (
        <IconButton
          aria-label="close"
          onClick={handleClose}
          className="subscription-close-button"
          size="small"
        >
          <CloseIcon />
        </IconButton>
      )}

      <DialogContent className="subscription-dialog-content">
        <Box className="subscription-content-wrapper">
          <Typography variant="h3" className="subscription-heading">
            Upgrade Your Plan
          </Typography>
          <Typography variant="body1" className="subscription-message">
            You have reached your plan's user limit. Please upgrade to add more users.
          </Typography>

          <Box className="plans-container">
            {plans.map((plan) => (
              <Box
                component="section"
                key={plan.id}
                className={`plan-card ${plan.isCurrent ? "current-plan" : ""}`}
              >
                {plan.savings && (
                  <Box component="span" className="plan-savings-badge">
                    {plan.savings}
                  </Box>
                )}

                <Typography component="h4" className="plan-name">
                  {plan.name}
                </Typography>

                <Box className="plan-pricing">
                  <Typography component="span" className="plan-currency">
                    $
                  </Typography>
                  <Typography component="span" className="plan-price">
                    {plan.price}
                  </Typography>
                  <Typography component="span" className="plan-period">
                    {plan.period}
                  </Typography>
                </Box>

                <Typography className="plan-description">
                  {plan.description}
                </Typography>

                {plan.isCurrent || plan.isFree ? (
                  <Button className="plan-action current-action" disabled fullWidth>
                    {plan.isCurrent ? "Your Current Plan" : "Free Plan"}
                  </Button>
                ) : (
                  <Button
                    className="plan-action upgrade-action"
                    variant="contained"
                    onClick={() => handleUpgrade(plan)}
                    disabled={isRenewing || !plan.stripePriceId}
                    fullWidth
                  >
                    {isRenewing && renewingPlanId === plan.id
                      ? "Processing..."
                      : "Upgrade"}
                  </Button>
                )}

                <Box component="ul" className="plan-features">
                  {plan.features.map((feature) => (
                    <Box component="li" key={feature} className="plan-feature">
                      <CheckIcon className="feature-check" />
                      <Typography component="span" className="span-feature">{feature}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default React.memo(SubscriptionExpiredPopup);
